import { getSessionStorage } from './subSessionStorage';
import { isMultiSessionEnabled } from './isMultiSessionEnabled';
import { WORKER_SUB_SESSION_EVENT } from './wireContract';
import type { WorkerSubSessionEventName } from './wireContract';

/**
 * Sub-session telemetry goes only to IBE's UI log beacon (`window.etiJsLog`), never to third-party
 * analytics or the dataLayer, and never holds the sub-session id or anything derived from it.
 */

type EtiJsLog = {
  log: (message: string, url: string, lineNumber?: number, component?: string) => void;
};

declare global {
  interface Window {
    etiJsLog?: EtiJsLog;
  }
}

export const SUB_SESSION_EVENT = {
  /** IBE assigned an id to a tab that held none. */
  MINTED: 'minted',
  /** IBE assigned an id replacing the one the tab held. */
  ADOPTED: 'adopted',
  /** Another live tab owned the candidate id, so this tab gave it up. */
  COLLISION: 'collision',
  /** Nobody objected to an id this tab only knew from the URL (a copied link whose tab is closed or frozen). */
  TAKEOVER: 'takeover',
  /** IBE minted an id for this document, so the tab used it at once, before its claim. */
  TRUSTED: 'trusted',
  /** Another live tab objected to an id used at once after all, so the tab gave it up. */
  TRUST_WITHDRAWN: 'trustWithdrawn',
  /** The flag is on, but a required browser API is missing. */
  UNSUPPORTED: 'unsupported',
  /** The page booted without a controlling worker. */
  UNCONTROLLED: 'uncontrolled',
  /** Claim duration, from candidate to settled (timing, sampled). */
  CLAIM: 'claim',
  /** Per-request worker overhead on GraphQL requests (timing, sampled). */
  REQUESTS: 'requests',
  // Seen by the worker only, and reported by a page it controls.
  WORKER_ADOPTED: WORKER_SUB_SESSION_EVENT.WORKER_ADOPTED,
  LEGACY: WORKER_SUB_SESSION_EVENT.LEGACY,
  DISABLED: WORKER_SUB_SESSION_EVENT.DISABLED,
  DISABLED_AFTER_RESTART: WORKER_SUB_SESSION_EVENT.DISABLED_AFTER_RESTART,
} as const;

export type SubSessionEventName = (typeof SUB_SESSION_EVENT)[keyof typeof SUB_SESSION_EVENT];

const WORKER_EVENT_NAMES: ReadonlySet<string> = new Set(Object.values(WORKER_SUB_SESSION_EVENT));

export const isWorkerSubSessionEvent = (name: unknown): name is WorkerSubSessionEventName =>
  typeof name === 'string' && WORKER_EVENT_NAMES.has(name);

/** Numbers and booleans only, so no id can end up in a payload. */
export type SubSessionEventData = Record<string, boolean | number>;

const LOG_PREFIX = '[SUB_SESSION]';
const LOG_COMPONENT = 'SubSession';

export const TIMING_SAMPLE_RATE = 0.1;
const TIMING_EVENTS: ReadonlySet<SubSessionEventName> = new Set([
  SUB_SESSION_EVENT.CLAIM,
  SUB_SESSION_EVENT.REQUESTS,
]);

/** How many GraphQL requests per page load the worker overhead aggregate covers. */
export const REQUEST_SAMPLE_SIZE = 10;
const GRAPHQL_PATH = '/graphql';

/** How long an uncontrolled page waits for a worker before reporting it never got one. */
export const CONTROL_WAIT_MS = 10000;

const reportedEvents = new Set<SubSessionEventName>();
let isTimingSampledPage: boolean | null = null;

/** Decided once per page load, so a page reports all its timings or none. */
const isTimingSampled = (): boolean => {
  if (isTimingSampledPage === null) {
    isTimingSampledPage = Math.random() < TIMING_SAMPLE_RATE;
  }
  return isTimingSampledPage;
};

const roundMs = (value: number): number => Math.round(Math.max(0, value) * 10) / 10;

/** Drops anything that is not a number or a boolean, even if a caller got past the type. */
const sanitise = (data: SubSessionEventData): SubSessionEventData =>
  Object.fromEntries(
    Object.entries(data).filter(
      ([, value]) =>
        typeof value === 'boolean' || (typeof value === 'number' && Number.isFinite(value)),
    ),
  );

// The pathname only, since the rest of a URL can carry the id in `ibe.ss`.
const toPathname = (path: string | undefined): string => {
  if (!path) {
    return window.location.pathname;
  }
  try {
    return new URL(path, window.location.origin).pathname;
  } catch {
    return window.location.pathname;
  }
};

/**
 * Reports an event at most once per page load. Returns whether this page takes care of it,
 * now or earlier. `path` is the page the event is about, when that is not this page.
 */
export const reportSubSessionEvent = (
  name: SubSessionEventName,
  data: SubSessionEventData = {},
  path?: string,
): boolean => {
  if (!isMultiSessionEnabled()) {
    return false;
  }
  if (reportedEvents.has(name)) {
    return true;
  }
  if (TIMING_EVENTS.has(name) && !isTimingSampled()) {
    return false;
  }

  const logger = window.etiJsLog;
  if (!logger || typeof logger.log !== 'function') {
    return false;
  }

  reportedEvents.add(name);
  try {
    logger.log(
      `${LOG_PREFIX} ${name} ${JSON.stringify(sanitise(data))}`,
      toPathname(path),
      undefined,
      LOG_COMPONENT,
    );
  } catch {
    // Telemetry must never break the page.
  }
  return true;
};

export const reportSubSessionAssigned = (previousId: string | null, assignedId: string): void => {
  if (!previousId) {
    reportSubSessionEvent(SUB_SESSION_EVENT.MINTED);
  } else if (previousId !== assignedId) {
    reportSubSessionEvent(SUB_SESSION_EVENT.ADOPTED);
  }
};

export const reportSubSessionClaimSettled = ({
  durationMs,
  isKept,
  windowMs,
}: {
  durationMs: number;
  isKept: boolean;
  windowMs: number;
}): void => {
  const timing = { durationMs: roundMs(durationMs), windowMs };
  if (!isKept) {
    reportSubSessionEvent(SUB_SESSION_EVENT.COLLISION, timing);
  }
  reportSubSessionEvent(SUB_SESSION_EVENT.CLAIM, { ...timing, isKept });
};

// Only an id from the URL counts as a takeover: a duplicate of a closed tab looks exactly
// like a reload, so that case is not detectable.
export const reportSubSessionClaimKept = ({
  isFromLinkOnly,
}: {
  isFromLinkOnly: boolean;
}): void => {
  if (isFromLinkOnly) {
    reportSubSessionEvent(SUB_SESSION_EVENT.TAKEOVER);
  }
};

export const reportSubSessionTrusted = (): void => {
  reportSubSessionEvent(SUB_SESSION_EVENT.TRUSTED);
};

// Expected never to happen, since IBE minted the id for this very document.
export const reportSubSessionTrustWithdrawn = (): void => {
  reportSubSessionEvent(SUB_SESSION_EVENT.TRUST_WITHDRAWN);
};

export const reportSubSessionUnsupported = (): void => {
  const support = {
    hasBroadcastChannel: typeof BroadcastChannel === 'function',
    hasServiceWorker: typeof navigator !== 'undefined' && Boolean(navigator.serviceWorker),
    hasSessionStorage: Boolean(getSessionStorage()),
  };
  if (Object.values(support).includes(false)) {
    reportSubSessionEvent(SUB_SESSION_EVENT.UNSUPPORTED, support);
  }
};

/**
 * Reports whether and when a worker took control of a page that booted uncontrolled.
 * `hasActiveWorker` means a hard reload; without it, a first install.
 */
export const observeSubSessionControl = (
  container: Pick<ServiceWorkerContainer, 'addEventListener'>,
  hasActiveWorker: boolean,
): void => {
  if (!isMultiSessionEnabled()) {
    return;
  }

  let isSettled = false;
  const settle = (isControlled: boolean): void => {
    if (isSettled) {
      return;
    }
    isSettled = true;
    clearTimeout(timeoutId);
    reportSubSessionEvent(SUB_SESSION_EVENT.UNCONTROLLED, {
      hasActiveWorker,
      isControlled,
      ...(isControlled ? { msToControl: roundMs(performance.now()) } : {}),
    });
  };

  const timeoutId = setTimeout(() => settle(false), CONTROL_WAIT_MS);
  container.addEventListener('controllerchange', () => settle(true), { once: true });
  window.addEventListener('pagehide', () => settle(false), { once: true });
};

const isGraphqlRequest = (entry: PerformanceResourceTiming): boolean => {
  try {
    const { pathname } = new URL(entry.name, window.location.origin);
    return pathname === GRAPHQL_PATH || pathname.startsWith(`${GRAPHQL_PATH}/`);
  } catch {
    return false;
  }
};

const median = (values: number[]): number | undefined =>
  values.length ? [...values].sort((a, b) => a - b)[Math.floor(values.length / 2)] : undefined;

const summariseRequests = (entries: PerformanceResourceTiming[]): SubSessionEventData => {
  // workerStart is 0 for requests the worker did not handle.
  const controlled = entries.filter((entry) => entry.workerStart > 0);
  const uncontrolled = entries.filter((entry) => entry.workerStart <= 0);
  const workerMs = controlled.map((entry) => roundMs(entry.fetchStart - entry.workerStart));
  const durationMs = (entry: PerformanceResourceTiming) =>
    roundMs(entry.responseEnd - entry.startTime);

  const summary: Record<string, number | undefined> = {
    count: entries.length,
    controlledCount: controlled.length,
    workerMedianMs: median(workerMs),
    workerMaxMs: workerMs.length ? Math.max(...workerMs) : undefined,
    controlledMedianMs: median(controlled.map(durationMs)),
    uncontrolledMedianMs: median(uncontrolled.map(durationMs)),
  };

  return Object.fromEntries(
    Object.entries(summary).filter((pair): pair is [string, number] => typeof pair[1] === 'number'),
  );
};

/**
 * Reports one aggregate of worker time and request durations, with and without the worker,
 * for the first GraphQL requests of a sampled page load.
 */
export const observeSubSessionRequests = (): void => {
  if (!isMultiSessionEnabled() || !isTimingSampled() || typeof PerformanceObserver !== 'function') {
    return;
  }

  const entries: PerformanceResourceTiming[] = [];
  let observer: PerformanceObserver | null = null;

  const flush = (): void => {
    observer?.disconnect();
    window.removeEventListener('pagehide', flush);
    if (entries.length) {
      reportSubSessionEvent(SUB_SESSION_EVENT.REQUESTS, summariseRequests(entries));
    }
  };

  try {
    observer = new PerformanceObserver((list) => {
      (list.getEntries() as PerformanceResourceTiming[])
        .filter(isGraphqlRequest)
        .forEach((entry) => {
          if (entries.length < REQUEST_SAMPLE_SIZE) {
            entries.push(entry);
          }
        });
      if (entries.length >= REQUEST_SAMPLE_SIZE) {
        flush();
      }
    });
    observer.observe({ type: 'resource', buffered: true });
    window.addEventListener('pagehide', flush);
  } catch {
    // No Resource Timing: nothing to measure.
  }
};
